import { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  IonLabel,
  IonRow,
  IonText,
  useIonToast,
} from "@ionic/react";
import TextEditor from "../editor";
import { isArray, unionBy } from "lodash";
import { Spinner, displayMsg } from "pymble-ui/components";
import { LessonDetail, useLessonDetail } from "../../hooks/useLessonDetail";
import { useForm, SubmitHandler } from "react-hook-form";
import CoverRoomSelect from "./CoverRoomSelect";

import "./CoverWorkForm.css";
import { home, homeOutline } from "ionicons/icons";
import PymbleFileUploader, { FileMetadata } from "./PymbleFileUploader";
import FileAttachment from "./FileAttachment";
import { useFileUtils } from "../../hooks/useFileUtils";

// *** CoverWork File operations & config ***
// Where do coverwork files get stored?
const COVERWORK_BLOB_CONTAINER = "secure/lesson";
// What is the name of the collection in Atlas DB that stores metadata?
const COVERWORK_COLLECTION_NAME = "LessonDetail";
// What are the tags to use when storing the file in Atlas DB?
const COVERWORK_FILE_TAGS = ["CoverWork", "Lesson"];
// Should the file be secure with a SAS token?
const COVERWORK_FILE_SECURE = true;

/**
 * Create a file upload id based on event.PeriodId, event.ClassId  and event.start (datetime)
 * in the format of "PeriodId-ClassId-YYYYMMDD"
 *
 * @param event
 * @returns string
 */
export const createFileUploadId = (lesson: LessonDetail) => {
  let id = "";
  if (lesson?.Class?.PeriodId && lesson?.Class?.ClassId && lesson?.LessonStartTime) {
    const date = new Date(lesson?.LessonStartTime);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    // Format month to 2 digits
    const monthStr = month < 10 ? `0${month}` : `${month}`;
    const day = date.getDate();
    const fileUploadId = `${lesson?.Class?.PeriodId}-${lesson?.Class?.ClassId}${year}${monthStr}${day}`;
    id = fileUploadId;
  }
  return id;
};

type CoverFormInputs = {
  room: string;
  reason: string;
  cover_summary: string;
  cover_detail: string;
  room_name?: string;
  files?: FileMetadata[];
};

interface ContainerProps extends RouteComponentProps<any> {
  lesson: LessonDetail | null;
  onLessonUpdated: (lesson: LessonDetail) => void;
  onLessonDeleted?: (lesson: LessonDetail) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
const CoverWorkForm: React.FC<ContainerProps> = ({
  lesson,
  onLessonUpdated,
  onLessonDeleted
}) => {
  const {
    register,
    handleSubmit,
    watch,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors },
    setValue,
    getValues,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reset,
  } = useForm<CoverFormInputs>();
  const [fileUploadId, setFileUploadId] = useState<string | null>(null);

  const { deleteFile } = useFileUtils();
  const [showToast] = useIonToast();


  useEffect(() => {
    // Make sure cover form has the correct values from selected lesson
    setValue("cover_summary", lesson?.CoverWork?.Summary || "Work for Lesson");
    setValue("cover_detail", lesson?.CoverWork?.Detail || "");
    setValue("room", lesson?.Room?.Swap?.ForeignKey || "");
    setValue("room_name", lesson?.Room?.Swap?.Short || "");
    setValue("reason", lesson?.Room?.Swap?.Reason || "");
  }, [lesson, setValue]);

  useEffect(() => {
    // Generate a file upload id for the file uploader
    if (lesson?.Class?.PeriodId && lesson?.Class?.ClassId) {
      const fileUploadId = createFileUploadId(lesson);
      setFileUploadId(fileUploadId);
    }
  }, [lesson]);

  const handleSwapRoomChange = (e: any) => {
    console.log("handleSwapRoomChange", e);
    setValue("room", e.value);
    setValue("room_name", e.valueText);
  };

  const { lessonSaving, updateLesson, deleteLesson } = useLessonDetail(lesson?._id);

  const handleFileUploadComplete = async (files: FileMetadata[]) => {
    console.log("CoverWorkForm: File upload complete", files);
    // Add the files to the lesson coverwork
    let newFiles = lesson?.CoverWork?.Files
      // eslint-disable-next-line no-unsafe-optional-chaining
      ? [...lesson?.CoverWork?.Files]
      : [];
    // If data has Files array add these to newFiles
    newFiles = [...newFiles, ...files];

    // Use lodash unionBy to merge and remove duplicate files
    newFiles = unionBy(newFiles, files, "InternalName");

    const formData = getValues();

    onSubmitCover({
      ...formData,
      files: newFiles,
    });
  };


  const handleFileDelete = async (file: FileMetadata) => {
    const result = await deleteFile(
      COVERWORK_BLOB_CONTAINER,
      file?.InternalName ?? ""
    );

    if (lesson && (result?.Result === "Deleted" || result?.Result === "NotFound")) {
      // Remove the file from the list
      const newFiles = lesson?.CoverWork?.Files ? lesson?.CoverWork?.Files.filter(
        (f: FileMetadata) => f?.InternalName !== file?.InternalName
      ) : [];
      let newLesson: LessonDetail = {
        _id: lesson?._id,
        CoverWork: { ...lesson?.CoverWork, Files: newFiles },
        RequiresCover: lesson?.RequiresCover, // Preserve the RequiresCover field
      };
      await updateLesson(newLesson);

      if (result?.Result === "NotFound") {
        // Give user a heads up
        const msg = "Could not find in storage. Removing anyway.";
        await showToast({ message: msg, color: "warning", duration: 3000 });
      }

      setValue("files", newFiles);

      const formData = getValues();

      const swapInfo = {
        Short: formData.room_name,
        ForeignKey: formData.room,
        Reason: formData.reason,
      };
      const coverWork = {
        Summary: formData.cover_summary,
        Detail: formData.cover_detail,
        // PrivateNotes: data.PrivateNotes,
        Files: newFiles,
      };
      newLesson = {
        ...lesson,
        Room: {
          ...lesson.Room,
          Swap: swapInfo,
        },
        CoverWork: {
          ...lesson.CoverWork,
          Summary: coverWork.Summary,
          Detail: coverWork.Detail,
          Files: coverWork.Files
        },
      };
      onLessonUpdated(newLesson);
    }
    return result;
  };


  const onSubmitCover: SubmitHandler<CoverFormInputs> = async (
    data: CoverFormInputs
  ) => {
    console.log("onSubmitCover", data);
    if (lesson) {
      let newFiles = lesson?.CoverWork?.Files
        // eslint-disable-next-line no-unsafe-optional-chaining
        ? [...lesson?.CoverWork?.Files]
        : [];
      // If data has Files array add these to newFiles
      if (isArray(data.files)) {
        newFiles = unionBy(newFiles, data.files, "InternalName");
      }

      const swapInfo = {
        Short: data.room_name,
        ForeignKey: data.room,
        Reason: data.reason,
      };
      const coverWork = {
        Summary: data.cover_summary,
        Detail: data.cover_detail,
        // PrivateNotes: data.PrivateNotes,
        Files: newFiles,
      };
      await updateLesson({
        _id: lesson._id,
        Room: {
          Swap: swapInfo,
        },
        CoverWork: coverWork,
        RequiresCover: lesson.RequiresCover, // Preserve the RequiresCover field
      });
      // setTimeout(refreshTeacherLessons, 100);

      displayMsg("Cover work saved", {
        type: "success",
        position: "middle",
        duration: 2000,
      });

      const newLesson: LessonDetail = {
        ...lesson,
        Room: {
          ...lesson.Room,
          Swap: swapInfo,
        },
        CoverWork: {
          ...lesson.CoverWork,
          Summary: coverWork.Summary,
          Detail: coverWork.Detail,
          Files: coverWork.Files
        },
      };
      onLessonUpdated(newLesson);
    }
  };

  const onDeleteLesson = async () => {
    if (lesson) {
      await deleteLesson(lesson);
      displayMsg("Lesson removed successfully", {
        type: "success",
        position: "middle",
        duration: 2000,
      });
      if (onLessonDeleted) {
        onLessonDeleted(lesson);
      }
    }
  }

  return (
    <div className="cover-work-form">
      <form onSubmit={handleSubmit(onSubmitCover)}>
        <IonRow>
          <IonCol>
            <IonItem lines="none" >
              <IonText>
                <h3>Room Swap</h3>
              </IonText>
              <IonText style={{
                "color": "var(--ion-color-medium)",
                "fontSize": "0.7rem",
                "marginTop": "0.5rem",
                "marginLeft": "1rem",
                "opacity": "0.7"
              }}>
                (Optional)
              </IonText>
            </IonItem>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            <IonGrid className="ion-no-padding cover-room-grid">
              <IonRow>
                <IonCol size="6">
                  <IonItem lines="none" className="cover-room-item">
                    <IonIcon icon={home} color="success" slot="start"></IonIcon>
                    <IonText>
                      {lesson?.Room?.Short}
                    </IonText>
                  </IonItem>
                </IonCol>
                <IonCol size="6">
                  <IonItem lines="none" className="cover-room-item">
                    <IonIcon icon={homeOutline} color="medium" slot="start"></IonIcon>
                    <CoverRoomSelect
                      value={watch("room_name") ?? lesson?.Room?.Swap?.Short}
                      onChange={handleSwapRoomChange}
                    />
                  </IonItem>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            <IonItem lines="none">
              <IonText>
                <h3>Cover Work</h3>
              </IonText>
            </IonItem>
            <IonItem lines="none" className="cover-work-item hidden"> {/* hide for now */}
              <IonInput
                {...register("cover_summary")}
                placeholder="Cover Work Summary"
                className="cover-summary"
              />
            </IonItem>
            <div className="cover-work-item cover-detail-editor">
              <TextEditor
                content={watch("cover_detail") || ""}
                placeholder="Cover Work Details"
                onChange={(value: string) => setValue("cover_detail", value)}
              />
            </div>
          </IonCol>
        </IonRow>

        <IonRow>
          <IonCol size="1"></IonCol>
          <IonCol>
            {lesson?.CoverWork?.Files &&
              lesson?.CoverWork?.Files?.map(
                (file: FileMetadata, i: number) => {
                  return (
                    <FileAttachment
                      key={file?.InternalName + "-" + i}
                      file={file}
                      onDelete={handleFileDelete}
                    />
                  );
                }
              )}
          </IonCol>
        </IonRow>
        <IonRow className="ion-align-items-center">
          <IonCol className="ion-align-self-center">
            {fileUploadId && (
              <PymbleFileUploader
                id={fileUploadId}
                settings={{
                  Container: COVERWORK_BLOB_CONTAINER,
                  Collection: COVERWORK_COLLECTION_NAME,
                  Secure: COVERWORK_FILE_SECURE,
                  Tags: COVERWORK_FILE_TAGS,
                }}
                showMessages={false}
                onUploadComplete={handleFileUploadComplete}
              />
            )}
          </IonCol>
        </IonRow>

        <IonRow>
          <IonCol>
            <IonItem lines="none">
              {lesson?.Type === "Event" && (
                <IonButton onClick={onDeleteLesson} color="danger" size="small" fill="outline" title="Delete this lesson">{"Delete"}</IonButton>
              )
              }
              <div slot="end">{lessonSaving && <Spinner />}</div>
              <IonButton
                type="submit"
                slot="end"
                size="small"
                disabled={lessonSaving}
              >
                {"Save"}
              </IonButton>
            </IonItem>
          </IonCol>
        </IonRow>
      </form>
    </div>
  );
};

export default withRouter(CoverWorkForm);
